/**
 * duck-gallery.js — Duck Shooting Gallery (GDD 6.5.1).
 *
 * Three conveyor rows of metal duck silhouettes cross the booth at 0.5, 1.2 and
 * 2.0 m/s in alternating directions, wrapping at the ends of the run. The player
 * holds a cork rifle and pulls the trigger; a raycast leaves the muzzle and the
 * distance from the hit point to that duck's bullseye decides whether it is a
 * 50 pt body hit or a 200 pt bullseye. A golden duck surfaces two or three times
 * a round for exactly two seconds and is worth 1000.
 *
 * The rifle — including how aiming differs between VR and desktop — lives in
 * `duck-rifle.js`. Everything here is conveyor, scoring and round shape.
 *
 * Every tuning number lives in constants.js DUCK_GALLERY.
 *
 * @module stalls/duck-gallery
 */

import * as THREE from 'three';
import { DUCK_GALLERY, HUB, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import { CorkRifle } from './duck-rifle.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import * as physics from '../core/physics.js';
import { input } from '../core/input.js';
import { bus } from '../core/events.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';
import { randRange, randInt } from '../core/testmode.js';

const CFG = DUCK_GALLERY;

/** Half the height of a factory duck — used to centre it on its holder. */
const DUCK_HALF_H = 0.10;
/** Collider half-extents around a centred duck silhouette. */
const DUCK_HALF = { x: 0.115, y: 0.10, z: 0.018 };
/** A point on the duck's body plate well clear of the bullseye, in duck space. */
const BODY_SPOT = new THREE.Vector3(-0.06, 0.06, 0.014);
/** Local +X of the stall — the axis a knocked duck hinges on. */
const AXIS_X = new THREE.Vector3(1, 0, 0);

class DuckGallery extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** @type {Array<object>} Every duck, row ducks and the golden one. */
    this.ducks = [];
    /** Body id → duck record, for resolving a raycast hit. */
    this._byBody = new Map();
    /** @type {object|null} */ this.golden = null;
    /** @type {number[]} Round-elapsed seconds at which a golden surfaces. */
    this._goldenTimes = [];
    this._goldenLeft = 0;

    this.rifle = new CorkRifle(this, { range: CFG.shotRange, flashTime: CFG.flashTime });
    this._sinceShot = CFG.fireInterval;
    this._shots = 0;
    this._hits = 0;
    this._offXR = null;

    this._plotMatrix = new THREE.Matrix4();
    this._plotQuat = new THREE.Quaternion();
    this._v = new THREE.Vector3();
    this._v2 = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    this._tilt = new THREE.Quaternion();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._cachePlotTransform();
    const width = HUB.boothWidth - 0.4;

    // Backdrop the ducks read against, plus a floor so a missed shot lands
    // somewhere rather than sailing out of the booth. Mid-tone on purpose: the
    // ducks are pale metal silhouettes and a dark board swallowed them.
    this._slab(width, 2.9, 0.1, { x: 0, y: 1.55, z: 5.6 }, 0x5a4f7d);
    this._slab(width, 0.1, 6.0, { x: 0, y: -0.05, z: 3.1 }, 0x6a5a44);

    for (const row of CFG.rows) this._buildRail(row);
    this._buildRail({ ...CFG.golden, key: 'golden' });

    // On the back wall above the ducks. Mounted near the counter it sat above
    // the top of the frame and lost its first line.
    // On the back wall at z 5.45. Height is set by SCREEN position, not world
    // position: the golden duck's rail is nearer the camera (z 4.30) so it
    // projects higher than its world Y suggests, and it crossed the readout
    // twice before this. Clearance needs
    //   (panelBottom − eye) / 5.45  >  (railTop − eye) / 4.30
    // which for railTop 2.385 and eye 1.62 puts the panel's bottom above 2.59.
    // At 2.725 it clears with room to spare and the top stays inside the booth.
    this.buildScoreboard({ y: 3.15, z: 5.45, width: 1.7, height: 0.85 });

    // In front of the rows rather than over them: a light behind the ducks only
    // rims them, and their faces — which is what you aim at — stay black.
    const light = createStallLight(0xffe0b0, 3.4, 14);
    light.position.set(0, 2.5, 1.5);
    this.root.add(light);

    this.rifle.build(this.dynamicRoot);
    // A player who enters VR mid-round should be able to pick the rifle up.
    this._offXR = bus.on(EVENTS.XR_SESSION_START, () => this.rifle.registerGrab());
  }

  /** A fixed box of booth structure. @private */
  _slab(w, h, d, pos, color) {
    const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), factory.mat(color, { rough: 0.95 }));
    box.receiveShadow = true;
    this.spawn(box, pos, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: w / 2, y: h / 2, z: d / 2 },
      position: this.localToWorld(pos.x, pos.y, pos.z),
    });
    return box;
  }

  /** @private */
  _buildRail(row) {
    const rail = new THREE.Mesh(
      new THREE.BoxGeometry(CFG.trackHalf * 2 + 0.5, 0.05, 0.16),
      factory.mat(0x3d4460, { rough: 0.6, metal: 0.4 }),
    );
    rail.position.set(0, row.y - DUCK_HALF_H - 0.04, row.z);
    this.root.add(rail);

    const label = factory.createSign('', {
      w: 0.62, h: 0.2, lines: [`${row.speed.toFixed(1)} m/s`], bg: '#12172a',
    });
    label.position.set(-CFG.trackHalf - 0.46, row.y, row.z - 0.14);
    label.rotation.y = Math.PI;
    this.root.add(label);
  }

  /** @private */
  _cachePlotTransform() {
    this.plot.root.updateWorldMatrix(true, false);
    this._plotMatrix.copy(this.plot.root.matrixWorld);
    this.plot.root.getWorldQuaternion(this._plotQuat);
  }

  /** Stall-local → world, into a shared scratch vector. @private */
  _toWorld(x, y, z) {
    return this._v2.set(x, y, z).applyMatrix4(this._plotMatrix);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ seconds: CFG.roundSeconds });
    this._cachePlotTransform();
    this.ducks = [];
    this._byBody.clear();
    this._sinceShot = CFG.fireInterval;
    this._shots = 0;
    this._hits = 0;

    for (const row of CFG.rows) {
      const spacing = (CFG.trackHalf * 2) / row.count;
      for (let i = 0; i < row.count; i++) {
        this._addDuck({
          id: `duck-${row.key}-${i}`, group: row.key, row,
          x: -CFG.trackHalf + i * spacing, golden: false,
        });
      }
    }

    // The golden duck exists all round but spends most of it parked out of
    // sight, so its appearances cost no allocation and leak nothing.
    const g = CFG.golden;
    this.golden = this._addDuck({
      id: 'duck-golden', group: 'golden', x: -CFG.trackHalf, golden: true,
      row: { key: 'golden', y: g.y, z: g.z, speed: g.speed, dir: -1 },
    });
    this.golden.state = 'down';
    this.golden.autoRespawn = false;
    this._goldenTimes = this._scheduleGolden();
    this._goldenLeft = 0;

    this.rifle.spawn();
  }

  /**
   * When the golden duck surfaces. The first is early enough that every round
   * shows one; the rest are spread over the remainder (GDD 6.5.1).
   * @returns {number[]} Round-elapsed seconds, ascending.
   * @private
   */
  _scheduleGolden() {
    const g = CFG.golden;
    const n = randInt(g.minPerRound, g.maxPerRound);
    const times = [randRange(g.firstWindow[0], g.firstWindow[1])];
    const [lo, hi] = g.laterWindow;
    const span = (hi - lo) / Math.max(1, n - 1);
    for (let i = 1; i < n; i++) times.push(randRange(lo + (i - 1) * span, lo + i * span));
    return times;
  }

  /** @private */
  _addDuck({ id, group, row, x, golden }) {
    const holder = new THREE.Group();
    holder.name = golden ? 'golden-duck' : 'duck';

    // The factory duck stands on its feet and faces +Z. Drop it half a body so
    // the holder origin is the silhouette's centre, then turn it around so the
    // bullseye faces the player.
    const model = factory.createDuckTarget(golden);
    model.position.y = -DUCK_HALF_H;
    model.rotation.y = Math.PI;
    holder.add(model);

    const bodySpot = new THREE.Object3D();
    bodySpot.position.copy(BODY_SPOT);
    model.add(bodySpot);

    // Freeze both aim anchors as offsets in the holder's own frame, while the
    // holder is still at the origin. Everything downstream then derives their
    // world positions from the BODY transform — the same transform the raycast
    // tests against — so aim, hit point and bullseye measurement can never
    // drift a frame apart from each other.
    holder.updateMatrixWorld(true);
    const bullseye = model.getObjectByName('bullseye');
    const bullOffset = bullseye.getWorldPosition(new THREE.Vector3());
    const bodyOffset = bodySpot.getWorldPosition(new THREE.Vector3());

    const handle = this.spawn(holder, { x, y: row.y, z: row.z }, {
      type: 'kinematic', shape: 'box', halfExtents: DUCK_HALF,
      position: this.localToWorld(x, row.y, row.z),
    });

    const duck = {
      id, row, x, golden, holder, handle, bullOffset, bodyOffset,
      bullseyeRadius: model.userData.bullseyeRadius,
      state: 'up', timer: 0, autoRespawn: true,
    };
    this.ducks.push(duck);
    this._byBody.set(handle.id, duck);
    this.registerTarget(handle, { id, group });
    return duck;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._sinceShot += dt;
    this._updateGolden(dt);
    this._updateDucks(dt);
    this.rifle.update(dt);

    // Holding the trigger auto-fires at the cap, which is what an unlimited-shot
    // 60-second round wants (GDD 6.5.1).
    for (const side of ['left', 'right']) {
      if (input.getHand(side)?.triggerPressed) { this.fire(side); break; }
    }
  }

  /** @private */
  _updateGolden(dt) {
    if (!this.golden) return;

    if (this.golden.state === 'up') {
      this._goldenLeft -= dt;
      if (this._goldenLeft <= 0) this.golden.state = 'down';
      return;
    }
    if (this.golden.state !== 'down') return;

    const elapsed = CFG.roundSeconds - this.timeLeft;
    if (this._goldenTimes.length === 0 || this._goldenTimes[0] > elapsed) return;
    this._goldenTimes.shift();
    this._raiseGolden();
  }

  /** Surface the golden duck for its window. @private @returns {string|null} */
  _raiseGolden() {
    if (!this.golden || !this.roundActive) return null;
    this.golden.state = 'up';
    this.golden.timer = 0;
    this.golden.handle.userData.scored = false;
    this._goldenLeft = CFG.golden.visibleSeconds;
    audio.play('golden-duck');
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'golden-duck' });
    return this.golden.id;
  }

  /** @private */
  _updateDucks(dt) {
    const half = CFG.trackHalf;
    for (const duck of this.ducks) {
      duck.x += duck.row.speed * duck.row.dir * dt;
      if (duck.x > half) duck.x -= half * 2;
      else if (duck.x < -half) duck.x += half * 2;

      if (duck.state === 'falling') {
        duck.timer += dt;
        if (duck.timer >= CFG.knockTime) { duck.state = 'down'; duck.timer = 0; }
      } else if (duck.state === 'down' && duck.autoRespawn) {
        duck.timer += dt;
        if (duck.timer >= CFG.respawnDelay) {
          duck.state = 'up';
          duck.timer = 0;
          duck.handle.userData.scored = false;
        }
      }
      this._poseDuck(duck);
    }
  }

  /**
   * Drive a duck's kinematic body toward this frame's pose.
   *
   * Deliberately `setNextKinematic*` rather than `setPosition`: the latter
   * teleports the body AND force-syncs the mesh immediately, which leaves the
   * duck you can see a frame ahead of the collider you can hit — about 3 cm on
   * the 2 m/s row. Handing Rapier a target instead keeps the rendered duck and
   * the raycastable duck the same object.
   * @private
   */
  _poseDuck(duck) {
    const body = duck.handle.body;
    if (duck.state === 'down') {
      // Parked below the floor: invisible, and out of every raycast's way.
      const parked = this._toWorld(duck.x, -30, duck.row.z);
      body.setNextKinematicTranslation({ x: parked.x, y: parked.y, z: parked.z });
      return;
    }
    const k = duck.state === 'falling' ? Math.min(1, duck.timer / CFG.knockTime) : 0;
    const p = this._toWorld(duck.x, duck.row.y - k * DUCK_HALF_H, duck.row.z);
    body.setNextKinematicTranslation({ x: p.x, y: p.y, z: p.z });
    // Tips backward into the booth, hinged near its base.
    this._tilt.setFromAxisAngle(AXIS_X, k * Math.PI * 0.5);
    this._q.copy(this._plotQuat).multiply(this._tilt);
    body.setNextKinematicRotation({ x: this._q.x, y: this._q.y, z: this._q.z, w: this._q.w });
  }

  // ── Shooting ────────────────────────────────────────────────────────────

  /**
   * Pull the trigger. Rate-limited to `DUCK_GALLERY.fireInterval`.
   * @param {'left'|'right'} [side] Which hand feels the recoil.
   * @returns {{hit: boolean, targetId: string|null, points: number, label: string}|null}
   *   null when the round is over or the shot fell inside the rate cap.
   */
  fire(side = 'right') {
    if (!this.roundActive || this._sinceShot < CFG.fireInterval) return null;
    const ray = this.rifle.aimRay();
    return ray ? this._shoot(ray, side) : null;
  }

  /**
   * Fire at a nominated point or duck. This is the automation entry point
   * behind `__cc.stalls.fireAt` — it runs the same raycast and the same scoring
   * code a trigger pull does; only the aim is chosen rather than mouse-driven.
   *
   * @param {string|number[]|{id?: string, spot?: 'body'|'bullseye',
   *   x?: number, y?: number, z?: number}} target A registered target id, a
   *   world point, or `{ id, spot }` to pick a duck's body plate or bullseye.
   * @returns {object|null}
   */
  fireAt(target) {
    if (!this.roundActive || this._sinceShot < CFG.fireInterval) return null;
    const point = this._resolveAim(target);
    if (!point) return null;
    const ray = this.rifle.aimAt(point);
    return ray ? this._shoot(ray, 'right') : null;
  }

  /** @private @returns {THREE.Vector3|null} A fresh world-space aim point. */
  _resolveAim(target) {
    if (Array.isArray(target) && target.length >= 3) {
      return new THREE.Vector3(target[0], target[1], target[2]);
    }
    const id = typeof target === 'string' ? target : target?.id;
    if (!id) {
      return target && Number.isFinite(target.x)
        ? new THREE.Vector3(target.x, target.y, target.z)
        : null;
    }
    const duck = this.ducks.find((d) => d.id === id);
    if (!duck || duck.state !== 'up') return null;
    const offset = target?.spot === 'bullseye' ? duck.bullOffset : duck.bodyOffset;
    return this._anchorWorld(duck, offset, new THREE.Vector3());
  }

  /**
   * World position of one of a duck's anchors, taken from its physics body.
   * @private @returns {THREE.Vector3} `out`.
   */
  _anchorWorld(duck, offset, out) {
    const t = duck.handle.body.translation();
    const r = duck.handle.body.rotation();
    return out.copy(offset)
      .applyQuaternion(this._q.set(r.x, r.y, r.z, r.w))
      .add(this._v.set(t.x, t.y, t.z));
  }

  /** @private */
  _shoot({ origin, dir }, side) {
    this._sinceShot = 0;
    this._shots += 1;
    this.rifle.flash(origin);
    audio.play('cork-shot');
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'cork-shot' });

    const hit = physics.raycast(origin, dir, CFG.shotRange, { exclude: this.rifle.handle });
    const duck = hit && hit.handle ? this._byBody.get(hit.handle.id) : null;
    if (!duck || duck.state !== 'up') {
      haptics.play(side, 'miss');
      this.updateScoreboard();
      return { hit: false, targetId: null, points: 0, label: 'miss' };
    }
    return this._scoreDuck(duck, hit.point, side);
  }

  /**
   * Body, bullseye or golden — decided by how far the hit landed from the
   * bullseye centre (GDD 6.5.1).
   * @private
   */
  _scoreDuck(duck, point, side) {
    const offset = point.distanceTo(this._anchorWorld(duck, duck.bullOffset, this._v2));

    let points = CFG.bodyPoints;
    let label = 'Duck';
    let color = '#ffd166';
    if (duck.golden) {
      points = CFG.goldenPoints; label = 'GOLDEN DUCK!'; color = '#d4af37';
    } else if (offset <= duck.bullseyeRadius) {
      points = CFG.bullseyePoints; label = 'Bullseye'; color = '#6fe7c4';
    }

    this._hits += 1;
    duck.handle.userData.scored = true;
    this.addScore(points, { position: point.clone(), label, color });
    audio.play('impact-hit', { volume: 0.9 });
    haptics.play(side, 'impact', { force: 18 });

    duck.state = 'falling';
    duck.timer = 0;
    if (duck.golden) this._goldenLeft = 0;

    return { hit: true, targetId: duck.id, points, label };
  }

  /** Internals for `__cc.stalls.debug()`. @returns {object} */
  debugState() {
    return {
      shots: this._shots,
      hits: this._hits,
      golden: this.golden
        ? { state: this.golden.state, secondsLeft: Math.max(0, this._goldenLeft) }
        : null,
      goldenPending: this._goldenTimes.length,
      ducks: this.ducks.map((d) => ({ id: d.id, state: d.state, x: d.x, speed: d.row.speed })),
    };
  }

  /**
   * Test affordances, reached through `__cc.stalls.fireAt` / `raiseGolden`.
   * Both route into the same methods gameplay uses.
   * @param {string} name @param {object} args
   */
  testHook(name, args = {}) {
    if (name === 'fire') return this.fireAt(args.target);
    if (name === 'golden') {
      // `only` drops the rest of the RNG schedule, so a test asserting on the
      // two-second window cannot have a scheduled appearance land inside it.
      if (args.only) this._goldenTimes = [];
      return this._raiseGolden();
    }
    return null;
  }

  // ── Presentation ────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const accuracy = this._shots > 0 ? Math.round((this._hits / this._shots) * 100) : 0;
    // Three lines, not four: hits and accuracy share one so the panel stays
    // short enough to sit above the conveyor rails without being occluded.
    this.scoreboard.setLines([
      `Score  ${this.score}`,
      `Time   ${Math.ceil(Math.max(0, this.timeLeft))}s`,
      `Hits   ${this._hits} / ${this._shots}  (${accuracy}%)`,
    ]);
  }

  onTeardown() {
    if (this._offXR) { this._offXR(); this._offXR = null; }
    this.rifle.dispose();
    this.ducks = [];
    this.golden = null;
    this._byBody.clear();
    this._goldenTimes = [];
  }
}

registerStall({
  id: 'duck-gallery',
  name: 'Duck Gallery',
  cost: CFG.cost,
  create: (ctx) => new DuckGallery(ctx),
});

export { DuckGallery };
